// src/pages/Home.jsx
// SEO-optimised homepage for Dynesourcer – Buy-to-Let Mortgage Sourcing & BTL Calculator
// Target keywords: buy-to-let mortgage, BTL mortgage comparison, buy-to-let calculator,
// landlord mortgage UK, buy-to-let mortgage rates, HMO mortgage, portfolio landlord mortgage,
// buy-to-let sourcing tool, specialist BTL broker, limited company buy-to-let

import React from "react";
import { Helmet } from "react-helmet-async";
import {
  UserPlus,
  Check,
  Shield,
  TrendingUp,
  Home as HomeIcon,
  Zap,
  Search,
  Lock,
  FileText,
  ArrowRight,
  Star,
  Calculator,
  BadgeCheck,
  Building2,
  MessageSquareHeart,
} from "lucide-react";
import { Link } from "react-router-dom";
import TopNav from "../../components/TopNav.jsx";
import SiteFooter from "../../components/SiteFooter.jsx";
import BorrowerProductSelectModal from "./BorrowerProductSelectModal.jsx";
import ReviewPromptModal from "../../components/reviews/ReviewPromptModal.jsx";
import { useReviewPrompt, REVIEW_PROMPT_DELAY_MS } from "../../hooks/useSourcingEngagement.js";

// ------------ Config (used by components & tests) -----------------
export const homeConfig = {
  products: [
    { key: "btl", title: "Buy-to-Let Mortgages", active: true, href: "/sourcing/buy-to-let-mortgage-sourcing", icon: "home" },
    { key: "invoice", title: "Invoice Finance", active: true, href: "/invoice-finance/invoice-finance-sourcing", icon: "file" },
    { key: "complex_btl", title: "Complex BTL", active: true, href: "/mortgages/complex-buy-to-let-mortgage-calculator", icon: "home" },
    { key: "semi_commercial", title: "Semi-commercial", active: false, href: "/semi-commercial-calculator-uk", icon: "building" },
    { key: "commercial", title: "Commercial", active: false, href: "/commercial-calculator-uk", icon: "building" },
    { key: "bridging", title: "Bridging Finance", active: false, href: "/sourcing/bridging", icon: "zap" },
    { key: "development", title: "Development Finance", active: false, href: "/sourcing/development", icon: "trend" },
  ],
};

// ---------------- Lightweight test helpers -----------------------
export const homeTests = [
  {
    id: "live-products",
    description: "BTL, Invoice Finance and Complex BTL are active; others are coming soon",
    expect: (cfg = homeConfig) => {
      const active = cfg.products.filter((p) => p.active).map((p) => p.key);
      return (
        active.includes("btl") &&
        active.includes("invoice") &&
        active.includes("complex_btl") &&
        active.length === 3
      );
    },
  },
  {
    id: "roadmap-order",
    description:
      "Roadmap order is BTL → Invoice → Complex BTL → Semi-commercial → Commercial → Bridging → Development",
    expect: (cfg = homeConfig) =>
      cfg.products.map((p) => p.key).join(",") ===
      "btl,invoice,complex_btl,semi_commercial,commercial,bridging,development",
  },
];

// ------------------------------- Page ------------------------------------
export default function Home() {
  const [showCookies, setShowCookies] = React.useState(() => {
    try {
      return localStorage.getItem("ds_cookies_ack") !== "1";
    } catch {
      return true;
    }
  });
  const {
    open: reviewPromptOpen,
    dismiss: dismissReviewPrompt,
    openNow: openReviewPrompt,
  } = useReviewPrompt({ storageKey: "homepage", delayMs: REVIEW_PROMPT_DELAY_MS });

  const handleAcceptCookies = () => {
    try {
      localStorage.setItem("ds_cookies_ack", "1");
      localStorage.setItem("ds_cookies_choice", "accept_all");
    } catch {}
    setShowCookies(false);
  };

  const handleRejectCookies = () => {
    try {
      localStorage.setItem("ds_cookies_ack", "1");
      localStorage.setItem("ds_cookies_choice", "reject_non_essential");
      localStorage.setItem(
        "ds_cookie_prefs",
        JSON.stringify({
          essential: true,
          analytics: false,
          marketing: false,
        })
      );
    } catch {}
    setShowCookies(false);
  };

  const handleSaveCookiePreferences = (prefs) => {
    try {
      localStorage.setItem("ds_cookies_ack", "1");
      localStorage.setItem("ds_cookies_choice", "managed");
      localStorage.setItem("ds_cookie_prefs", JSON.stringify(prefs));
    } catch {}
    setShowCookies(false);
  };

  const title = "Buy-to-Let Mortgage Sourcing & BTL Calculator UK | Dynesourcer";
  const description =
    "Compare buy-to-let mortgage products across the UK, check true costs with our BTL calculator, and progress to broker-triaged applications on Dynesourcer.";
  const canonical =
    typeof window !== "undefined" ? `${window.location.origin}/` : "https://dynesourcer.co.uk/";

  React.useEffect(() => {
    const preload = (href) => {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = href;
      document.head.appendChild(link);
      return () => { document.head.removeChild(link); };
    };
    const cleanupBorrower = preload("/I_Borrower.png");
    const cleanupLender = preload("/I_lender.png");
    return () => { cleanupBorrower(); cleanupLender(); };
  }, []);

  return (
    <main className="min-h-screen text-slate-800 bg-white">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={canonical} />
      </Helmet>
      <TopNav />
      <SplitHero />
      <TrustBar />
      <WhyChooseUs />
      <MobileAppShowcase />
      <OurProducts />
      <InvoiceFinanceTeaser />
      <TestimonialsSection />
      <RewardsSection />
      <HowItWorks />
      <FAQSection />
      <DarkKPIBand />
      {showCookies && (
        <CookiesBanner
          onAccept={handleAcceptCookies}
          onReject={handleRejectCookies}
          onSavePreferences={handleSaveCookiePreferences}
        />
      )}
      <button
        type="button"
        onClick={openReviewPrompt}
        className="fixed bottom-5 left-5 z-40 inline-flex items-center gap-2 rounded-full bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg hover:bg-sky-700"
      >
        <MessageSquareHeart className="h-4 w-4" />
        Feedback
      </button>
      <ReviewPromptModal
        open={reviewPromptOpen}
        onClose={dismissReviewPrompt}
        contextLabel="How was your visit to Dynesourcer?"
        defaultType="General Feedback"
      />
      <SiteFooter />
    </main>
  );
}

// ─── Trust bar ────────────────────────────────────────────────────────────────
function TrustBar() {
  const items = [
    { label: "5,500+ BTL products sourced", short: "5,500+ products" },
    { label: "Whole-of-market access", short: "Whole-of-market" },
    { label: "FCA-regulated brokerage", short: "FCA-regulated" },
    { label: "Free to enquire online", short: "Free enquire" },
    { label: "Expert triage on every case", short: "Expert triage" },
  ];
  return (
    <div className="bg-sky-600 text-white py-2 sm:py-2.5 md:py-3">
      <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
        <ul className="flex flex-nowrap items-center justify-center gap-x-1.5 sm:gap-x-4 md:gap-x-8 overflow-x-auto scrollbar-none text-[9px] sm:text-xs md:text-sm font-semibold leading-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {items.map((it) => (
            <li key={it.label} className="inline-flex items-center gap-0.5 sm:gap-1.5 whitespace-nowrap shrink-0">
              <Check className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 flex-shrink-0 text-sky-100" aria-hidden="true" />
              <span>
                <span className="md:hidden">{it.short}</span>
                <span className="hidden md:inline">{it.label}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Split hero ───────────────────────────────────────────────────────────────
function SplitHero() {
  const [productModalOpen, setProductModalOpen] = React.useState(false);

  return (
    <section
      className="relative w-full bg-slate-50 max-md:pt-[5.5rem]"
      aria-label="Finance sourcing and lender portal"
    >
      {/* ── H1 for SEO (screen-reader visible, visually hidden above the two panels) ── */}
      <h1 className="sr-only">
        Mortgage &amp; Invoice Finance Sourcing Tool UK | Dynesourcer
      </h1>

      <div className="grid grid-cols-2 w-full">
        {/* Borrower / Landlord panel — opens product picker */}
        <button
          type="button"
          onClick={() => setProductModalOpen(true)}
          className="group relative h-[260px] sm:h-[360px] md:h-[480px] overflow-hidden w-full text-left"
          aria-label="Choose a product to source as a borrower"
        >
          <img
            src="/I_Borrower.webp"
            alt="Borrower comparing mortgage and invoice finance products on Dynesourcer"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
            style={{ objectPosition: "center 20%" }}
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-sky-900/90 via-sky-900/60 to-transparent" />
          <div className="relative z-10 h-full w-full flex flex-col items-center justify-center md:justify-end pb-0 md:pb-16 text-white text-center px-1.5 sm:px-4 md:px-6">
            <div className="inline-flex items-center justify-center h-8 w-8 sm:h-10 sm:w-10 md:h-16 md:w-16 rounded-full bg-white/20 backdrop-blur-sm mb-1.5 sm:mb-2 md:mb-4">
              <HomeIcon className="h-4 w-4 sm:h-5 sm:w-5 md:h-8 md:w-8" />
            </div>
            <p className="uppercase tracking-widest text-[9px] sm:text-[10px] md:text-xs font-bold opacity-90">I am a</p>
            <h2 className="mt-0.5 md:mt-2 text-base sm:text-3xl md:text-6xl font-black leading-tight">BORROWER</h2>
            <p className="mt-4 text-base leading-relaxed max-w-md opacity-95 hidden md:block">
              Compare mortgage and invoice finance products by eligibility and true cost.
              Source BTL, invoice finance and more, then apply online — our broker team triages every case.
            </p>
            <div className="mt-3 sm:mt-4 md:mt-6 inline-flex items-center gap-1.5 md:gap-2 text-[11px] sm:text-sm font-semibold group-hover:gap-3 transition-all">
              Choose a product <ArrowRight className="h-3.5 w-3.5 md:h-4 md:w-4" />
            </div>
          </div>
        </button>

        {/* Lender panel */}
        <Link
          to="/lenders"
          className="group relative h-[260px] sm:h-[360px] md:h-[480px] overflow-hidden"
          aria-label="Access the lender portal"
        >
          <img
            src="/I_lender.webp"
            alt="Lender managing finance products on the Dynesourcer portal"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/60 to-transparent" />
          <div className="relative z-10 h-full w-full flex flex-col items-center justify-center md:justify-end pb-0 md:pb-16 text-white text-center px-1.5 sm:px-4 md:px-6">
            <div className="inline-flex items-center justify-center h-8 w-8 sm:h-10 sm:w-10 md:h-16 md:w-16 rounded-full bg-white/20 backdrop-blur-sm mb-1.5 sm:mb-2 md:mb-4">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 md:h-8 md:w-8" />
            </div>
            <p className="uppercase tracking-widest text-[9px] sm:text-[10px] md:text-xs font-bold opacity-90">I am a</p>
            <h2 className="mt-0.5 md:mt-2 text-base sm:text-3xl md:text-6xl font-black leading-tight">LENDER</h2>
            <p className="mt-4 text-base leading-relaxed max-w-md opacity-95 hidden md:block">
              List your mortgage and invoice finance products on our sourcing platform. Monitor
              time-to-cash, approval rates, and product performance in real time.
            </p>
            <div className="mt-3 sm:mt-4 md:mt-6 inline-flex items-center gap-1.5 md:gap-2 text-[11px] sm:text-sm font-semibold group-hover:gap-3 transition-all">
              Access lender portal <ArrowRight className="h-3.5 w-3.5 md:h-4 md:w-4" />
            </div>
          </div>
        </Link>
      </div>

      <BorrowerProductSelectModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
      />
    </section>
  );
}

// ─── Why choose us ────────────────────────────────────────────────────────────
function WhyChooseUs() {
  const items = [
    {
      icon: <Search className="h-6 w-6" />,
      title: "Whole-of-Market BTL Sourcing",
      desc: "Compare buy-to-let mortgage products from across the UK market filtered by your actual eligibility — rates, ICR, LTV, property type and more."
    },
    {
      icon: <Calculator className="h-6 w-6" />,
      title: "Free BTL Mortgage Calculator",
      desc: "Instantly calculate monthly repayments, interest cover ratio (ICR), arrangement fees and true cost of borrowing for any buy-to-let product."
    },
    {
      icon: <BadgeCheck className="h-6 w-6" />,
      title: "Expert Broker Triage",
      desc: "Every online application is reviewed by a qualified mortgage broker before it reaches the lender, reducing declines and saving you time."
    },
    {
      icon: <Building2 className="h-6 w-6" />,
      title: "Specialist BTL Criteria",
      desc: "HMOs, MUFBs, portfolio landlords, limited company SPVs and first-time landlords all supported with specialist lender criteria matched in real time."
    },
  ];

  return (
    <section
      id="why-dynesourcer"
      className="py-20 md:py-28 bg-white"
      aria-label="Why choose Dynesourcer for buy-to-let mortgage sourcing"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900">
            The smarter way to source<br />
            <span className="text-sky-600">buy-to-let mortgages</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Dynesourcer gives landlords, portfolio investors and property developers direct access
            to buy-to-let mortgage products matched by eligibility — backed by an expert broker
            who checks every application before submission.
          </p>
        </div>

        <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => (
            <div
              key={it.title}
              className="group relative rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm transition-all hover:shadow-xl hover:-translate-y-1 hover:border-sky-200"
            >
              <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-50 to-sky-100 text-sky-600 transition-transform group-hover:scale-110">
                {it.icon}
              </div>
              <h3 className="mt-6 text-lg font-bold text-slate-900">{it.title}</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Platform showcase ────────────────────────────────────────────────────────
function MobileAppShowcase() {
  return (
    <section
      className="relative py-20 md:py-28 bg-gradient-to-br from-indigo-900 via-sky-900 to-slate-900 text-white overflow-hidden"
      aria-label="Buy-to-let mortgage sourcing platform features"
    >
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtMS4xLjktMiAyLTJzMiAuOSAyIDItLjkgMi0yIDItMi0uOS0yLTJ6bS0yMCAwYzAtMS4xLjktMiAyLTJzMiAuOSAyIDItLjkgMi0yIDItMi0uOS0yLTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-40" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="min-w-0">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black leading-tight">
              Compare buy-to-let mortgage rates — free, instant, no broker calls required
            </h2>
            <p className="mt-6 text-lg text-slate-200 leading-relaxed">
              Our BTL sourcing platform lets you filter products by LTV, ICR, property type,
              landlord experience, and arrangement fees — so you only see mortgages you can
              actually get. Use the built-in buy-to-let calculator to compare the true cost
              of every deal before you apply.
            </p>

            <ul className="mt-8 space-y-4">
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <Check className="h-6 w-6 text-sky-400" />
                </div>
                <div>
                  <p className="font-semibold text-white">Filter by rate, fees &amp; ICR</p>
                  <p className="text-sm text-slate-300">Sort by initial rate, revert rate, arrangement fees or total cost over term</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <Check className="h-6 w-6 text-sky-400" />
                </div>
                <div>
                  <p className="font-semibold text-white">Specialist BTL criteria built in</p>
                  <p className="text-sm text-slate-300">HMO, MUFB, portfolio landlord, SPV/limited company, first-time landlord</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <Check className="h-6 w-6 text-sky-400" />
                </div>
                <div>
                  <p className="font-semibold text-white">Shortlist &amp; apply in minutes</p>
                  <p className="text-sm text-slate-300">Save your favourite BTL products and our broker validates before submitting to the lender</p>
                </div>
              </li>
            </ul>

            <div className="mt-10">
              <Link
                to="/sourcing/buy-to-let-mortgage-sourcing"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-500 px-8 py-4 text-lg font-bold text-white hover:bg-sky-600 transition-colors shadow-lg hover:shadow-xl"
              >
                Source BTL mortgages now <ArrowRight className="h-5 w-5" />
              </Link>
              <p className="mt-4 text-sm text-slate-300">
                Free to use &nbsp;•&nbsp; No registration required &nbsp;•&nbsp; Works on any device
              </p>
            </div>
          </div>

          {/* Laptop/tablet mockup (click to open BTL sourcing) */}
          <Link
            to="/sourcing/buy-to-let-mortgage-sourcing"
            aria-label="Open buy-to-let mortgage sourcing page"
            className="relative lg:h-[600px] flex items-center justify-center min-w-0 max-w-full"
          >
            <div className="relative z-10 w-full max-w-[520px] mx-auto transform hover:scale-105 transition-transform duration-500">
              <div className="relative">
                <div className="relative bg-slate-900 rounded-t-xl p-2 sm:p-3 shadow-2xl border-2 sm:border-4 border-slate-800">
                  <div className="w-full aspect-[520/340] max-h-[280px] sm:max-h-[340px] bg-white rounded-lg overflow-hidden relative">
                    <div className="absolute top-0 left-0 right-0 h-8 bg-slate-100 border-b border-slate-200 flex items-center px-3 gap-2 z-10">
                      <div className="flex gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                        <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                      </div>
                      <div className="flex-1 mx-3 bg-white rounded px-2 py-0.5 text-[10px] text-slate-500 flex items-center gap-1">
                        <Lock className="h-2.5 w-2.5" />
                        <span>dynesourcer.co.uk/mortgages/buy-to-let-mortgage-calculator</span>
                      </div>
                    </div>
                    <div className="w-full h-full pt-8 overflow-y-auto">
                      <img
                        src="/sourcing_screenshot.png"
                        alt="Dynesourcer buy-to-let mortgage sourcing interface showing product comparison"
                        className="w-full h-auto object-cover object-top"
                        loading="eager"
                      />
                    </div>
                  </div>
                </div>
                <div className="relative h-2 sm:h-3 bg-gradient-to-b from-slate-800 to-slate-900 rounded-b-xl" />
                <div className="relative h-1.5 sm:h-2 bg-slate-800" style={{ clipPath: 'polygon(5% 0%, 95% 0%, 100% 100%, 0% 100%)' }} />
              </div>
            </div>

            <div className="absolute right-0 top-1/2 -translate-y-1/2 transform rotate-6 opacity-50 hidden xl:block pointer-events-none">
              <div className="relative bg-slate-900 rounded-2xl p-2 shadow-xl border-4 border-slate-800">
                <div className="w-[280px] h-[200px] bg-gradient-to-br from-sky-400 to-indigo-500 rounded-xl overflow-hidden flex items-center justify-center">
                  <div className="text-center text-white p-6">
                    <TrendingUp className="h-12 w-12 mx-auto mb-3" />
                    <p className="font-bold text-sm">Works on all devices</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-sky-500/20 rounded-full blur-3xl" />
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-indigo-500/20 rounded-full blur-3xl" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── Our products ─────────────────────────────────────────────────────────────
function OurProducts() {
  const products = homeConfig.products;
  return (
    <section
      id="products"
      className="bg-slate-50 py-20 md:py-28"
      aria-label="Buy-to-let and specialist mortgage products"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900">
            Specialist mortgage products<br />for property investors
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            From standard buy-to-let to complex HMOs, bridging and development finance —
            we're building the UK's most comprehensive sourcing platform for property investors.
            <br />
            <span className="text-sm text-slate-500 mt-1 block">
              Launch roadmap: BTL → Invoice Finance → Complex BTL → Semi-commercial → Commercial → Bridging → Development
            </span>
          </p>
        </div>

        <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <ProductCard
              key={p.key}
              icon={
                p.icon === "home" ? <HomeIcon className="h-6 w-6" /> :
                p.icon === "file" ? <FileText className="h-6 w-6" /> :
                p.icon === "zap"  ? <Zap className="h-6 w-6" /> :
                p.icon === "building" ? <Building2 className="h-6 w-6" /> :
                                    <TrendingUp className="h-6 w-6" />
              }
              title={p.title}
              desc={
                p.key === "btl"
                  ? "Compare buy-to-let mortgage rates from across the UK market. Source purchase, remortgage and product transfer deals for individual and limited company landlords."
                  : p.key === "invoice"
                    ? "Release working capital tied up in unpaid invoices. Flexible selective and whole-ledger facilities for property and construction businesses."
                    : p.key === "complex_btl"
                      ? "Specialist BTL mortgages for HMOs, MUFBs, multi-unit blocks and complex borrower structures including portfolio landlords and SPVs."
                      : p.key === "semi_commercial"
                        ? "Finance for mixed-use properties with residential and commercial elements — shops with flats above, and similar semi-commercial assets."
                        : p.key === "commercial"
                          ? "Commercial mortgage sourcing for offices, retail, industrial and investment properties with tailored lender criteria."
                          : p.key === "bridging"
                            ? "Fast short-term bridging loans for auction purchases, refurbishment, downsizing or chain-break scenarios — regulated and unregulated."
                            : "Ground-up development and heavy refurbishment finance with staged drawdowns and flexible exit strategies."
              }
              bullets={
                p.key === "btl"
                  ? ["Up to 75% LTV available", "Portfolio & limited company landlords", "Free BTL calculator included"]
                  : p.key === "invoice"
                    ? ["Advance up to 90% of invoice value", "Selective or full ledger", "Fast 24–48 hr onboarding"]
                    : p.key === "complex_btl"
                      ? ["HMO & MUFB specialist criteria", "SPV / Ltd company structures", "Flexible ICR & DSCR calculations"]
                      : p.key === "semi_commercial"
                        ? ["Mixed-use & shop-with-flat", "Flexible income assessment", "Specialist lender panel"]
                        : p.key === "commercial"
                          ? ["Office, retail & industrial", "Investment & owner-occupy", "Tailored underwriting"]
                          : p.key === "bridging"
                            ? ["Terms from 1–24 months", "Quick credit decisions", "Regulated & unregulated"]
                            : ["Staged drawdown funding", "Independent monitoring surveyor", "Flexible exit planning"]
              }
              href={p.href}
              active={p.active}
              ctaLabel={
                p.key === "btl"
                  ? "Source BTL mortgages now"
                  : p.key === "invoice"
                    ? "Source invoice finance now"
                    : "Source now"
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductCard({
  icon,
  title,
  desc,
  bullets,
  href = "#",
  active = false,
  ctaLabel = "Source now",
}) {
  return (
    <article
      className={
        "group relative rounded-2xl border bg-white p-8 shadow-sm transition-all hover:shadow-xl hover:-translate-y-1" +
        (active ? " border-sky-200 hover:border-sky-300" : " border-slate-200 opacity-75")
      }
    >
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-sky-50 to-sky-100 text-sky-600">
        {icon}
      </div>
      <h3 className="mt-6 text-xl font-bold text-slate-900">{title}</h3>
      <p className="mt-3 text-slate-600 leading-relaxed">{desc}</p>

      <ul className="mt-6 space-y-3 text-sm text-slate-700">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-3">
            <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-sky-500" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8">
        {active ? (
          <Link
            to={href}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-500 px-6 py-3 text-sm font-bold text-white hover:bg-sky-600 transition-colors"
          >
            <Search className="h-4 w-4" /> {ctaLabel}
          </Link>
        ) : (
          <span
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-100 px-6 py-3 text-sm font-bold text-slate-500 cursor-not-allowed"
            title="Coming soon"
            aria-disabled="true"
          >
            <Lock className="h-4 w-4" /> Coming soon
          </span>
        )}
      </div>
    </article>
  );
}

// ─── Testimonials ─────────────────────────────────────────────────────────────
function TestimonialsSection() {
  const testimonials = [
    {
      text: "Found a competitive buy-to-let mortgage rate in minutes. The broker team reviewed my case before it went to the lender — no surprises.",
      author: "Sarah M.",
      role: "Portfolio Landlord",
      rating: 5
    },
    {
      text: "As a lender, the analytics portal gives us real-time visibility on time-to-cash and product performance. Exactly what we needed.",
      author: "James T.",
      role: "Senior Underwriter",
      rating: 5
    },
    {
      text: "Sourced an HMO mortgage I couldn't find anywhere else. The criteria matching saved me weeks of phoning around brokers.",
      author: "Michael R.",
      role: "HMO Investor",
      rating: 5
    },
    {
      text: "First-time landlord — the platform made BTL mortgages feel accessible. The triage team confirmed the right product before submission.",
      author: "Lisa K.",
      role: "First-time Landlord",
      rating: 5
    },
  ];

  return (
    <section
      className="bg-white py-20 md:py-28"
      aria-label="Customer reviews of Dynesourcer BTL mortgage sourcing"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900">
            What landlords say about Dynesourcer
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            From first-time landlords to portfolio investors — see why property professionals
            choose Dynesourcer for buy-to-let mortgage sourcing.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {testimonials.map((testimonial, idx) => (
            <blockquote key={idx} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed mb-4">
                "{testimonial.text}"
              </p>
              <footer className="border-t border-slate-100 pt-4">
                <p className="font-bold text-slate-900">{testimonial.author}</p>
                <p className="text-xs text-slate-500">{testimonial.role}</p>
              </footer>
            </blockquote>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex items-baseline gap-2">
            <span className="text-4xl font-black text-slate-900">Excellent</span>
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-amber-400 text-amber-400" />
              ))}
            </div>
          </div>
          <p className="mt-2 text-sm text-slate-600">Based on 2,847 reviews from landlords and property investors</p>
        </div>
      </div>
    </section>
  );
}

// ─── The Dynesourcer difference ───────────────────────────────────────────────
function RewardsSection() {
  return (
    <section
      className="relative py-20 md:py-28 bg-gradient-to-br from-slate-50 to-sky-50 overflow-hidden"
      aria-label="How Dynesourcer differs from other BTL mortgage comparison sites"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block px-4 py-2 bg-sky-100 text-sky-700 rounded-full text-sm font-bold mb-4">
              More than a BTL comparison site
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-tight">
              Sourcing + brokerage<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-indigo-600">
                in one platform
              </span>
            </h2>

            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Most buy-to-let comparison sites show you rates and leave you to figure out the rest.
              Dynesourcer combines intelligent BTL mortgage sourcing with expert broker triage
              — so your application reaches the right lender, first time.
            </p>

            <ul className="mt-8 space-y-4 text-slate-700">
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100">
                    <Check className="h-4 w-4 text-sky-600" />
                  </div>
                </div>
                <p className="leading-relaxed">
                  <span className="font-bold text-slate-900">Eligibility-first matching</span> — only see buy-to-let mortgage products you actually qualify for, filtered by LTV, ICR, property type and landlord profile
                </p>
              </li>
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100">
                    <Check className="h-4 w-4 text-sky-600" />
                  </div>
                </div>
                <p className="leading-relaxed">
                  <span className="font-bold text-slate-900">Specialist products covered</span> — HMO mortgages, MUFB, limited company BTL (SPV), portfolio landlord, and complex income types
                </p>
              </li>
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100">
                    <Check className="h-4 w-4 text-sky-600" />
                  </div>
                </div>
                <p className="leading-relaxed">
                  <span className="font-bold text-slate-900">Broker validates before submission</span> — a qualified mortgage broker reviews every application, checks product fit and confirms best rate before it goes to the lender
                </p>
              </li>
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/sourcing/buy-to-let-mortgage-sourcing"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-500 px-6 py-3 text-base font-bold text-white hover:bg-sky-600 transition-colors shadow-lg hover:shadow-xl"
              >
                Start sourcing BTL mortgages <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/about" className="inline-flex items-center justify-center gap-2 rounded-lg bg-white border-2 border-slate-200 px-6 py-3 text-base font-bold text-slate-700 hover:border-sky-300 hover:bg-sky-50 transition-colors">
                About us
              </Link>
            </div>
          </div>

          {/* Visual mockup - triage board */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-sky-500/10 rounded-3xl blur-3xl transform scale-105" />
            <div className="relative bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
              <div className="h-12 border-b border-slate-100 bg-slate-50 flex items-center px-4 gap-2">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                </div>
                <div className="ml-4 h-2 w-32 bg-slate-200 rounded-full" />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="h-4 w-48 bg-slate-200 rounded animate-pulse" />
                  <div className="h-8 w-8 bg-sky-100 rounded-full flex items-center justify-center">
                    <UserPlus className="h-4 w-4 text-sky-600" />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="p-4 rounded-xl border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-md transition-all cursor-default">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-2 w-2 rounded-full bg-emerald-500" />
                      <span className="text-xs font-bold text-slate-700">Application Validated — BTL Purchase 75% LTV</span>
                    </div>
                    <div className="h-2 w-3/4 bg-slate-200 rounded mb-2" />
                    <div className="h-2 w-1/2 bg-slate-200 rounded" />
                  </div>
                  <div className="p-4 rounded-xl border border-sky-100 bg-sky-50/50 hover:shadow-md transition-all">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-2 w-2 rounded-full bg-sky-500 animate-pulse" />
                      <span className="text-xs font-bold text-sky-700">Under Review — HMO Remortgage</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="h-2 w-24 bg-slate-200 rounded" />
                        <div className="h-2 w-12 bg-slate-200 rounded" />
                      </div>
                      <div className="h-2 w-full bg-slate-200 rounded" />
                    </div>
                  </div>
                  <div className="p-4 rounded-xl border border-slate-100 bg-slate-50 opacity-60">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-2 w-2 rounded-full bg-slate-300" />
                      <span className="text-xs font-bold text-slate-500">Draft — Ltd Company BTL</span>
                    </div>
                    <div className="h-2 w-1/2 bg-slate-200 rounded" />
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-4 border border-slate-100 animate-bounce" style={{ animationDuration: '3s' }}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900">Broker Verified</div>
                  <div className="text-xs text-slate-500">Every application checked</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Invoice finance launch ───────────────────────────────────────────────────
function InvoiceFinanceTeaser() {
  const highlights = [
    { title: "Up to 90% invoice purchase", detail: "Release cash against approved debtor invoices" },
    { title: "Selective or whole-ledger", detail: "Choose the facility structure that fits your cash flow" },
    { title: "Transparent total cost", detail: "Compare APR, service fees and net advance before you apply" },
  ];

  return (
    <section
      id="invoice-finance"
      className="relative overflow-hidden bg-slate-950 py-16 md:py-20 text-white"
      aria-label="Invoice Finance sourcing on Dynesourcer"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 80% 60% at 10% 20%, rgba(14,165,233,0.35), transparent 55%), radial-gradient(ellipse 70% 50% at 90% 80%, rgba(20,184,166,0.22), transparent 50%)",
        }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.05fr,0.95fr] lg:items-center lg:gap-14">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-500/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-sky-300">
              Now live on Dynesourcer
            </div>

            <h2 className="mt-5 text-3xl md:text-4xl lg:text-[2.75rem] font-black text-white leading-tight tracking-tight">
              Invoice Finance sourcing
              <span className="block text-sky-300">built for working capital</span>
            </h2>

            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl">
              Compare invoice finance facilities by net advance, fees and true cost —
              then shortlist and apply with broker triage, the same way you source mortgages.
            </p>

            <ul className="mt-7 space-y-4">
              {highlights.map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/20 text-teal-300">
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  <div>
                    <div className="font-semibold text-white">{item.title}</div>
                    <div className="text-sm text-slate-400 leading-snug">{item.detail}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/invoice-finance/invoice-finance-sourcing"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-500 px-6 py-3.5 text-base font-bold text-white hover:bg-sky-400 transition-colors shadow-lg shadow-sky-900/40"
              >
                Source invoice finance <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </Link>
              <Link
                to="/borrowers#products"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/5 px-6 py-3.5 text-base font-semibold text-white hover:bg-white/10 transition-colors"
              >
                View all products
              </Link>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-md lg:max-w-none">
            <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-5 md:p-6 shadow-2xl backdrop-blur-sm">
              <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-500/15 text-sky-300">
                    <FileText className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white truncate">Invoice finance facility</p>
                    <p className="text-xs text-slate-400">Illustrative comparison result</p>
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-teal-500/15 px-2.5 py-1 text-[11px] font-bold text-teal-300">
                  IP 90%
                </span>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-slate-950/60 border border-white/5 px-3.5 py-3">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">APR</div>
                  <div className="mt-1 text-2xl font-black tabular-nums text-white">5.62%</div>
                </div>
                <div className="rounded-xl bg-slate-950/60 border border-white/5 px-3.5 py-3">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Net advance</div>
                  <div className="mt-1 text-2xl font-black tabular-nums text-white">£3.33M</div>
                </div>
                <div className="rounded-xl bg-slate-950/60 border border-white/5 px-3.5 py-3">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Review limit</div>
                  <div className="mt-1 text-lg font-bold tabular-nums text-slate-100">£4.1M</div>
                </div>
                <div className="rounded-xl bg-slate-950/60 border border-white/5 px-3.5 py-3">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Min. service fee</div>
                  <div className="mt-1 text-lg font-bold tabular-nums text-slate-100">£2,500</div>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-sky-400/20 bg-sky-500/10 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-sky-100">Cost over product term</span>
                  <span className="text-lg font-black tabular-nums text-white">£186,420</span>
                </div>
                <p className="mt-1 text-[11px] text-sky-200/70 leading-snug">
                  Fees only — facility principal not included
                </p>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-slate-300">
                  Selective invoice discounting
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-slate-300">
                  Whole ledger
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-slate-300">
                  Additional borrowing
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── How it works ─────────────────────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    {
      num: "1",
      title: "Enter your BTL requirements",
      desc: "Tell us your property value, loan amount, rental income, property type, and borrower structure (individual, portfolio, or limited company)."
    },
    {
      num: "2",
      title: "Compare matched BTL mortgage products",
      desc: "See real buy-to-let mortgage rates you're eligible for, filtered by LTV, ICR and arrangement fees. Use our BTL calculator to check true cost."
    },
    {
      num: "3",
      title: "Broker validates your application",
      desc: "Our qualified mortgage broker reviews your case, confirms the best-fit product, and flags anything before submission — reducing declines."
    },
    {
      num: "4",
      title: "Application submitted &amp; tracked",
      desc: "We submit your buy-to-let mortgage application directly to the lender. Track progress end-to-end from your dashboard."
    },
  ];

  return (
    <section
      className="bg-gradient-to-br from-slate-50 to-sky-50 py-20 md:py-28"
      aria-label="How buy-to-let mortgage sourcing works on Dynesourcer"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900">
            How to source a buy-to-let mortgage on Dynesourcer
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            From comparison to completion — a simple, transparent process built for landlords and property investors.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, idx) => (
            <div key={idx} className="relative text-center">
              <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-sky-500 text-2xl font-black text-white">
                {step.num}
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
              <p className="text-sm text-slate-600" dangerouslySetInnerHTML={{ __html: step.desc }} />
              {idx < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-sky-300" />
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/sourcing/buy-to-let-mortgage-sourcing"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-sky-500 px-8 py-4 text-base font-bold text-white hover:bg-sky-600 transition-colors shadow-lg hover:shadow-xl"
          >
            Compare BTL mortgages now <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────
// Uses <details>/<summary> which Google reads as FAQ schema-eligible content
function FAQSection() {
  const faqs = [
    {
      q: "What is a buy-to-let mortgage and how does it work?",
      a: "A buy-to-let (BTL) mortgage is a loan specifically designed for landlords purchasing property to rent out rather than live in. Unlike a residential mortgage, eligibility is assessed primarily on projected rental income — lenders typically require rental income to cover 125%–145% of the monthly interest at a stressed rate. Most BTL mortgages are interest-only, meaning you pay only the interest each month and repay the full capital at the end of the term."
    },
    {
      q: "How much deposit do I need for a buy-to-let mortgage in the UK?",
      a: "Most buy-to-let mortgage lenders require a minimum deposit of 25% (75% LTV), though some specialist products are available at higher LTVs. If you're a portfolio landlord or using a limited company (SPV), some lenders may require 30–40% deposit. Larger deposits generally unlock lower buy-to-let mortgage rates."
    },
    {
      q: "Can I get a buy-to-let mortgage through a limited company?",
      a: "Yes. Limited company (SPV) buy-to-let mortgages have grown significantly as landlords seek tax efficiencies. Dynesourcer includes criteria filtering for limited company BTL applications, including SPV structure, director guarantees and Companies House status. Our broker team can guide you through the application process."
    },
    {
      q: "What is the buy-to-let calculator and how do I use it?",
      a: "Our free BTL mortgage calculator lets you input the property value, loan amount, expected rental income and mortgage term to instantly see monthly repayments, interest cover ratio (ICR), arrangement fee impact, and true cost over the initial product period. This helps you compare deals by actual cost rather than just headline rate."
    },
    {
      q: "Does Dynesourcer cover HMO and portfolio landlord mortgages?",
      a: "Yes. Dynesourcer is built for specialist BTL scenarios including HMOs (Houses in Multiple Occupation), multi-unit freehold blocks (MUFBs), and portfolio landlords with four or more mortgaged properties. Our sourcing criteria filter matches you only to lenders whose criteria your case fits."
    },
    {
      q: "Is there a fee to use Dynesourcer?",
      a: "Sourcing and submitting an online enquiry through Dynesourcer is completely free — there's no cost to compare buy-to-let mortgage products, use the BTL calculator, or shortlist deals. A broker fee applies when you proceed to a full mortgage application, which covers expert case preparation, lender submission and end-to-end support through to offer. You'll be made aware of any fee before you commit."
    },
  ];

  return (
    <section
      id="faq"
      className="bg-white py-20 md:py-28"
      aria-label="Frequently asked questions about buy-to-let mortgages"
    >
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
          Buy-to-let mortgage FAQs
        </h2>
        <p className="text-lg text-slate-600 mb-12">
          Common questions from landlords and property investors about buy-to-let mortgages,
          our sourcing tool and BTL calculator.
        </p>

        <div className="space-y-6">
          {faqs.map((faq, idx) => (
            <details
              key={idx}
              className="group rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <summary className="flex cursor-pointer items-center justify-between font-bold text-slate-900 list-none">
                <span>{faq.q}</span>
                <span className="ml-4 text-sky-500 transition-transform group-open:rotate-180">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">{faq.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── KPI band ─────────────────────────────────────────────────────────────────
function DarkKPIBand() {
  const stats = [
    { k: "£100M+", l: "BTL Loans Facilitated" },
    { k: "5,500+", l: "Products Sourced" },
    { k: "98%", l: "Application Success Rate" },
    { k: "24hrs", l: "Average Broker Response" },
  ];

  return (
    <section
      className="bg-gradient-to-br from-slate-900 via-sky-900 to-slate-900 text-slate-100 relative overflow-hidden"
      aria-label="Dynesourcer key statistics"
    >
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDE2YzAtMS4xLjktMiAyLTJzMiAuOSAyIDItLjkgMi0yIDItMi0uOS0yLTJ6bS0yMCAwYzAtMS4xLjktMiAyLTJzMiAuOSAyIDItLjkgMi0yIDItMi0uOS0yLTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((s) => (
            <div key={s.k} className="group">
              <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-sky-300 mb-2">
                {s.k}
              </div>
              <div className="text-sm md:text-base text-slate-300">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Cookies banner ───────────────────────────────────────────────────────────
function CookiesBanner({ onAccept, onReject, onSavePreferences }) {
  const [showManage, setShowManage] = React.useState(false);
  const [prefs, setPrefs] = React.useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  return (
    <div className="fixed inset-x-3 bottom-3 z-50 flex justify-end pointer-events-none pb-[max(0.25rem,env(safe-area-inset-bottom))] sm:inset-x-auto sm:left-auto sm:right-4 sm:bottom-4 sm:pb-0">
      <div className="pointer-events-auto w-full max-h-[min(70dvh,32rem)] overflow-y-auto rounded-2xl border border-slate-800 bg-slate-900/95 shadow-2xl backdrop-blur px-4 py-3 sm:w-[24rem] sm:max-w-sm sm:px-5 sm:py-4">
        <div className="flex flex-col gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-white">
              Cookies on Dynesourcer
            </h2>
            <p className="mt-2 text-xs sm:text-[13px] text-slate-200 leading-relaxed">
              Dynesourcer uses cookies to help our website function properly and to improve your browsing experience. Cookies are small files stored on your device that help us understand how visitors use our website and enable certain features to work.
            </p>
            <p className="mt-2 text-xs sm:text-[13px] text-slate-300 leading-relaxed">
              Some cookies are essential for the operation of the site, while others help us analyse usage and improve our services. You can manage or disable non-essential cookies through your browser settings. For more information, please see our{" "}
              <a href="/privacy" className="underline decoration-sky-400 text-sky-300 hover:text-sky-200">
                Cookies Policy
              </a>.
            </p>
          </div>
          {showManage ? (
            <div className="rounded-xl border border-slate-700 bg-slate-800/70 p-3">
              <div className="space-y-2 text-xs text-slate-200">
                <label className="flex items-center justify-between gap-3">
                  <span>Essential cookies</span>
                  <input type="checkbox" checked disabled />
                </label>
                <label className="flex items-center justify-between gap-3">
                  <span>Analytics cookies</span>
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) =>
                      setPrefs((prev) => ({ ...prev, analytics: e.target.checked }))
                    }
                  />
                </label>
                <label className="flex items-center justify-between gap-3">
                  <span>Marketing cookies</span>
                  <input
                    type="checkbox"
                    checked={prefs.marketing}
                    onChange={(e) =>
                      setPrefs((prev) => ({ ...prev, marketing: e.target.checked }))
                    }
                  />
                </label>
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowManage(false)}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-600 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => onSavePreferences({ ...prefs, essential: true })}
                  className="inline-flex items-center justify-center rounded-lg bg-sky-400 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-500"
                >
                  Save preferences
                </button>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-stretch sm:justify-end">
            <button
              type="button"
              onClick={onReject}
              className="inline-flex items-center justify-center rounded-lg border border-slate-500 px-4 py-2.5 text-xs sm:text-sm font-semibold text-slate-200 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
            >
              Reject
            </button>
            <button
              type="button"
              onClick={() => setShowManage((v) => !v)}
              className="inline-flex items-center justify-center rounded-lg border border-sky-400/60 px-4 py-2.5 text-xs sm:text-sm font-semibold text-sky-200 hover:bg-sky-900/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
            >
              Manage cookies
            </button>
            <button
              type="button"
              onClick={onAccept}
              className="col-span-2 inline-flex items-center justify-center rounded-lg bg-sky-400 px-4 py-2.5 text-xs sm:text-sm font-semibold text-white shadow hover:bg-sky-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 sm:col-span-1"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}